import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  AlertTypes,
  setAlert,
  Grid,
  Typography,
  Paper,
  Button,
  Box,
  Select,
  MenuItem,
  CircularProgress,
  DynamicDatagrid,
  GridColDef,
  Chip,
  MuiIcons,
} from '@leap4/frontend';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { urlService } from '../../routing/urlsService';
import ReportHeader from '../common/ReportHeader/ReportHeader';
import { AXIOS_INSTANCE } from '../../../api/axiosInstance';
import { useListConversionUploadBatches } from '../../../api/generated/api';
import type { BatchRecordDto } from '../../../api/generated/models';

const { CloudUpload: UploadIcon } = MuiIcons;

const ACCEPTED_FILE_TYPES = '.xlsx,.csv';
const EXPECTED_COLUMNS_HELP =
  'Expected columns (MB export): Reference Number, Status, Purchase Date, Order Value, Commission, Products Purchased. ' +
  'All sheets are read and every status is ingested; the file is rejected entirely if any sheet has an unrecognized or missing header.';

interface Campaign {
  id_campaign: string;
  name: string | null;
}

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const statusColor = (
  status?: string
): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
      return 'info';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const ConversionUploadsModule = () => {
  const dispatch = useAppDispatch();
  const selectedOrganizationId = useAppSelector((state) => state.auth.selectedOrganizationId);
  const history = useHistory();
  const queryClient = useQueryClient();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [missingDays, setMissingDays] = useState<{
    firstDate: string | null;
    lastDate: string;
    missingDates: string[];
  } | null>(null);
  const [missingLoading, setMissingLoading] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const response = await AXIOS_INSTANCE.get('/campaigns', {
        params: { scope: 'organization' },
      });
      setCampaigns(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      dispatch(setAlert({ msg: 'Could not fetch campaigns', type: AlertTypes.FAILURE, d: dispatch }));
    } finally {
      setLoadingCampaigns(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns, selectedOrganizationId]);

  useEffect(() => {
    if (!selectedCampaign || campaigns.length === 0) return;
    if (!campaigns.some((c) => c.id_campaign === selectedCampaign)) {
      setSelectedCampaign('');
    }
  }, [campaigns, selectedCampaign]);

  useEffect(() => {
    if (!selectedCampaign) {
      setMissingDays(null);
      return undefined;
    }
    let cancelled = false;
    setMissingLoading(true);
    AXIOS_INSTANCE.get('/conversion-uploads/missing-days', {
      params: { id_campaign: selectedCampaign },
    })
      .then((res) => {
        if (!cancelled) setMissingDays(res.data);
      })
      .catch(() => {
        if (!cancelled) setMissingDays(null);
      })
      .finally(() => {
        if (!cancelled) setMissingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCampaign]);

  const batchesQuery = useListConversionUploadBatches(
    selectedCampaign ? { id_campaign: selectedCampaign } : undefined,
    { query: { staleTime: 0 } }
  );
  const batches = batchesQuery.data?.batches || [];

  const selectedCampaignName = useMemo(() => {
    const c = campaigns.find((x) => x.id_campaign === selectedCampaign);
    return c ? c.name || c.id_campaign : selectedCampaign;
  }, [campaigns, selectedCampaign]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedCampaign) return;
    setDownloadingTemplate(true);
    try {
      const res = await AXIOS_INSTANCE.get('/conversion-uploads/template', {
        params: { id_campaign: selectedCampaign },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = (res.headers['content-disposition'] as string) || '';
      const match = /filename="?([^"]+)"?/.exec(disposition);
      link.download = match ? match[1] : `${selectedCampaign}_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      dispatch(setAlert({ msg: 'Could not download template', type: AlertTypes.FAILURE, d: dispatch }));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedCampaign || !selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('id_campaign', selectedCampaign);
      formData.append('file', selectedFile);

      const response = await AXIOS_INSTANCE.post(
        '/conversion-uploads',
        formData,
        { headers: { 'Content-Type': undefined } },
      );
      const result = response.data as { batchId: string };
      dispatch(setAlert({
        msg: `Upload accepted for "${selectedCampaignName}". Processing batch...`,
        type: AlertTypes.SUCCESS,
        d: dispatch,
      }));
      setSelectedFile(null);
      await queryClient.invalidateQueries(['/conversion-uploads/batches']);
      if (result?.batchId) {
        history.push(urlService.conversionUploadBatch(result.batchId));
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to upload file';
      dispatch(setAlert({
        msg: Array.isArray(message) ? message.join(', ') : message,
        type: AlertTypes.FAILURE,
        d: dispatch,
      }));
    } finally {
      setUploading(false);
    }
  };

  const columns: GridColDef<BatchRecordDto>[] = [
    {
      field: 'filename',
      headerName: 'File',
      width: 220,
      flex: 1,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            fontWeight: 500,
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() =>
            params.row.id && history.push(urlService.conversionUploadBatch(params.row.id))
          }
        >
          {params.row.filename || params.row.id}
        </Box>
      ),
    },
    { field: 'idCampaign', headerName: 'Campaign', width: 120 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.row.status || '—'} color={statusColor(params.row.status)} size="small" />
      ),
    },
    { field: 'rowCount', headerName: 'Rows', width: 90, type: 'number' },
    { field: 'createdCount', headerName: 'Created', width: 90, type: 'number' },
    { field: 'updatedCount', headerName: 'Updated', width: 90, type: 'number' },
    { field: 'duplicateCount', headerName: 'Dupes', width: 90, type: 'number' },
    { field: 'errorCount', headerName: 'Errors', width: 90, type: 'number' },
    {
      field: 'uploadedAt',
      headerName: 'Uploaded',
      width: 180,
      renderCell: (params) => formatDateTime(params.row.uploadedAt),
    },
  ];

  const rows = batches.map((b) => ({ id: b.id, ...b }));

  const uploadDisabled = !selectedCampaign || !selectedFile || uploading;

  return (
    <>
      <ReportHeader
        hideTimeNavbar={true}
        breadcrumbs={[
          { link: urlService.conversionUploads(), text: 'Conversion Uploads', icon: <UploadIcon /> },
        ]}
      />
      <Grid container>
        <Grid item xs={12}>
          <Paper sx={{ margin: '10px', padding: '30px', borderRadius: '12px' }} elevation={1}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Upload Conversions
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'center' }}>
              Select a campaign, then upload the client conversion file. Campaign is required before a
              file can be chosen.
            </Typography>

            <Grid container spacing={3} alignItems="center" sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Campaign (required)
                </Typography>
                <Select
                  variant="outlined"
                  fullWidth
                  value={selectedCampaign}
                  displayEmpty
                  disabled={loadingCampaigns}
                  onChange={(e) => setSelectedCampaign(String(e.target.value))}
                >
                  <MenuItem value="">
                    {loadingCampaigns ? 'Loading campaigns...' : 'Choose a campaign'}
                  </MenuItem>
                  {campaigns.map((c) => (
                    <MenuItem key={c.id_campaign} value={c.id_campaign}>
                      {c.name ? `${c.name} (${c.id_campaign})` : c.id_campaign}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  File (.xlsx / .csv)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={!selectedCampaign}
                  sx={{ height: 56, textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  {selectedFile ? selectedFile.name : 'Choose file'}
                  <input
                    type="file"
                    hidden
                    accept={ACCEPTED_FILE_TYPES}
                    disabled={!selectedCampaign}
                    onChange={handleFileChange}
                  />
                </Button>
              </Grid>

              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 2, height: 56, mt: 2.5 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                  onClick={handleUpload}
                  disabled={uploadDisabled}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 4 }}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
                {selectedCampaign && (
                  <Button
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {downloadingTemplate ? 'Preparing…' : 'Download template'}
                  </Button>
                )}
              </Grid>
            </Grid>

            {selectedCampaign && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Uploading to: <strong>{selectedCampaignName}</strong>
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              {EXPECTED_COLUMNS_HELP}
            </Typography>
          </Paper>
        </Grid>

        {selectedCampaign && (
          <Grid item xs={12}>
            <Paper sx={{ margin: '10px', padding: '20px', borderRadius: '12px' }} elevation={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Days Missing Verified Installs
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                Dates from the first verified install
                {missingDays?.firstDate ? ` (${missingDays.firstDate})` : ''} to today
                {missingDays?.lastDate ? ` (${missingDays.lastDate})` : ''} with no verified install
                for <strong>{selectedCampaignName}</strong>. Dates before the first install are not counted.
              </Typography>
              {missingLoading ? (
                <CircularProgress size={20} />
              ) : !missingDays || missingDays.firstDate === null ? (
                <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.7 }}>
                  No verified installs recorded yet for this campaign.
                </Typography>
              ) : missingDays.missingDates.length === 0 ? (
                <Chip
                  label="No missing days — every day has verified installs"
                  color="success"
                  size="small"
                />
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#ed6c02' }}>
                    {missingDays.missingDates.length} missing day(s)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {missingDays.missingDates.map((d) => (
                      <Chip key={d} label={d} color="warning" size="small" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{ margin: '10px', borderRadius: '12px' }} elevation={1}>
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Upload Batches
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedCampaign
                  ? `Batches for "${selectedCampaignName}"`
                  : 'All batches across campaigns'}
              </Typography>
            </Box>
            <DynamicDatagrid
              rows={rows}
              columns={columns}
              loading={batchesQuery.isLoading}
              refreshFunc={() => batchesQuery.refetch()}
              loadError={batchesQuery.isError}
              pageSize={25}
              rowHeight={45}
              style={{ minHeight: '55vh', width: '100%' }}
            />
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default ConversionUploadsModule;
